import {useEffect} from 'react'
import {useSelector} from 'react-redux'
import {selectFilterDetailsLoading} from '../../../store/slices/breadCrumbsSlice'
import GlobalSearchFilter from './GlobalSearchFilter'
import tooltipText from '../../common/TooltipText.json'
import {useFilterListDetail} from '../../../store/hooks/filterListDetail'
import {useGlobalSearch} from '../../../store/hooks/globalSearch'
import {
  selectAvailableFilters,
  selectCustomizations,
  selectBenefitsPermissions,
  selectAddedFilters,
  selectBenefitsEligibility
} from '../../../store/slices/filterManagementSlice'
import {selectFilterConfig} from '../../../store/slices/configSlice'
import {getFilterConfig, getCategoryLabel} from '../../../utils/filterManagementHelpers'

/**
 * Build map of filters that have mutual exclusivity defined in config
 */
const buildMutuallyExclusiveFiltersMap = (filterConfig) => {
  const mutuallyExclusiveFiltersMap = new Map()
  if (!filterConfig) return mutuallyExclusiveFiltersMap

  const benefitsConfig = getFilterConfig('receivingBenefits', filterConfig)
  if (benefitsConfig?.options) {
    for (const option of benefitsConfig.options) {
      if (option.mutuallyExclusiveWith) {
        mutuallyExclusiveFiltersMap.set(option.mutuallyExclusiveWith, true)
      }
    }
  }
  return mutuallyExclusiveFiltersMap
}

/**
 * Get the mutually exclusive filter keys that are currently conflicting
 */
const getActiveConflicts = (benefitsEligibility, filterConfig) => {
  const activeConflicts = new Set()
  if (!benefitsEligibility?.selected?.length || !filterConfig) return activeConflicts

  const benefitsConfig = getFilterConfig('receivingBenefits', filterConfig)
  if (benefitsConfig?.options) {
    for (const option of benefitsConfig.options) {
      if (benefitsEligibility.selected.includes(option.value) && option.mutuallyExclusiveWith) {
        activeConflicts.add(option.mutuallyExclusiveWith)
      }
    }
  }
  return activeConflicts
}

/**
 * Build display name for a filter, adding category name if it has mutual exclusivity
 */
const buildFilterDisplayName = (filter, customName, hasMutualExclusivity, filterConfig, customizations) => {
  const displayName = customName || filter.title || filter.FILTER_NAME

  if (hasMutualExclusivity) {
    const categoryName = getCategoryLabel(String(filter.FILTER_CATEGORY_ID), filterConfig, customizations)
    return categoryName ? `${displayName} - ${categoryName}` : displayName
  }

  return displayName
}

/**
 * Build unified filter options from filter list data
 * Shows category name for filters that have mutual exclusivity defined in config
 */
const buildUnifiedFilterOptions = (
  filterListData,
  availableFilters,
  customizations,
  filterConfig,
  benefitsEligibility
) => {
  const allFilters = [...(filterListData || [])]

  const mutuallyExclusiveFiltersMap = buildMutuallyExclusiveFiltersMap(filterConfig)
  const activeConflicts = getActiveConflicts(benefitsEligibility, filterConfig)

  return allFilters
    .filter((filter) => availableFilters.includes(filter.FILTER_KEY))
    .map((filter) => {
      const customName = customizations.filterNames?.[filter.FILTER_KEY]
      const hasMutualExclusivity = mutuallyExclusiveFiltersMap.has(filter.FILTER_KEY)
      const isCurrentlyConflicting = activeConflicts.has(filter.FILTER_KEY)

      const finalDisplayName = buildFilterDisplayName(
        filter,
        customName,
        hasMutualExclusivity,
        filterConfig,
        customizations
      )

      return {
        filterKey: filter.FILTER_KEY,
        filterName: finalDisplayName,
        label: filter.FILTER_KEY,
        title: filter.title,
        categoryId: filter.FILTER_CATEGORY_ID,
        FILTER_ID: filter.FILTER_ID,
        FILTER_CUSTOM_NAME: customName,
        disabled: isCurrentlyConflicting,
        tooltipText: isCurrentlyConflicting ? tooltipText.benefitsAlreadySelected : null
      }
    })
}

/**
 * Build outreach simulation options from filter config and permissions
 * Shows category name only when there's mutual exclusivity for clarity
 */
const buildOutreachSimulationOptions = (filterConfig, benefitsPermissions, addedFilters, customizations) => {
  const options = []
  if (!filterConfig) return options

  // Get Benefits Eligibility options
  const benefitsConfig = getFilterConfig('receivingBenefits', filterConfig)
  if (benefitsConfig?.options) {
    for (const option of benefitsConfig.options) {
      const hasPermission = benefitsPermissions.includes(option.id)
      const mutuallyExclusiveKey = option.mutuallyExclusiveWith
      const isEligibilityFilterAdded = mutuallyExclusiveKey && addedFilters.includes(mutuallyExclusiveKey)
      const isDisabled = !hasPermission || isEligibilityFilterAdded

      let disabledReason = null
      if (!hasPermission) {
        disabledReason = tooltipText.filterDisabled
      } else if (isEligibilityFilterAdded) {
        disabledReason = tooltipText.benefitsAlreadySelected
      }

      // Add category name ONLY if there's mutual exclusivity defined
      let displayName = option.label
      if (mutuallyExclusiveKey) {
        const categoryName = getCategoryLabel('8', filterConfig, customizations)
        displayName = `${option.label} - ${categoryName}`
      }

      options.push({
        filterKey: 'receivingBenefits',
        filterName: displayName,
        label: option.label,
        value: option.value,
        optionId: option.id,
        section: 'OS',
        categoryId: '8',
        tooltipText: disabledReason,
        disabled: isDisabled,
        mutuallyExclusiveWith: mutuallyExclusiveKey
      })
    }
  }

  return options
}

const GlobalSearch = () => {
  const {setGlobalSearchFilterOptions} = useGlobalSearch()
  const {filterListData} = useFilterListDetail()
  const filterDetailsLoading = useSelector(selectFilterDetailsLoading)
  const availableFilters = useSelector(selectAvailableFilters)
  const customizations = useSelector(selectCustomizations)
  const filterConfig = useSelector(selectFilterConfig)
  const benefitsPermissions = useSelector(selectBenefitsPermissions)
  const addedFilters = useSelector(selectAddedFilters)
  const benefitsEligibility = useSelector(selectBenefitsEligibility)

  // Build filter options from data sources (static filter metadata only)
  useEffect(() => {
    if (filterDetailsLoading) return
    if (!filterListData) return

    const unifiedFilterOptions = buildUnifiedFilterOptions(
      filterListData,
      availableFilters,
      customizations,
      filterConfig,
      benefitsEligibility
    )
    const outreachSimulationOptions = buildOutreachSimulationOptions(
      filterConfig,
      benefitsPermissions,
      addedFilters,
      customizations
    )
    const allOptions = [...unifiedFilterOptions, ...outreachSimulationOptions]
    if (allOptions.length > 0) {
      setGlobalSearchFilterOptions(allOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterDetailsLoading,
    filterListData,
    availableFilters,
    customizations,
    filterConfig,
    benefitsPermissions,
    addedFilters,
    benefitsEligibility
  ])

  return <GlobalSearchFilter />
}

export default GlobalSearch

/**
 * List of functions and their purpose:
 *
 * - useEffect: Builds unified global search filter options from filterListData,
 *   applying permissions, customizations, and tracking added/disabled state from filterManagement.
 *   Also includes Outreach Simulation filters built from filterConfig and permissions.
 */
